import React from 'react';
import { CheckCircle, Calendar, Clock, Car, Mail, Phone, Download, Plus } from 'lucide-react';
import type { BookingState } from './bookingTypes';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const SLOT_LABELS: Record<string, string> = {
  'ts-0800': '8:00 AM', 'ts-0900': '9:00 AM', 'ts-1000': '10:00 AM',
  'ts-1100': '11:00 AM', 'ts-1200': '12:00 PM', 'ts-1300': '1:00 PM',
  'ts-1400': '2:00 PM', 'ts-1500': '3:00 PM', 'ts-1600': '4:00 PM', 'ts-1700': '5:00 PM',
};

interface BookingConfirmationProps {
  booking: BookingState;
  onNewBooking: () => void;
}

export default function BookingConfirmation({ booking, onNewBooking }: BookingConfirmationProps) {
  const formatDate = (d: string) => {
    const [y, m, day] = d.split('-').map(Number);
    return `${MONTHS[m - 1]} ${day}, ${y}`;
  };

  return (
    <div className="max-w-2xl mx-auto animate-slide-up">
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} className="text-emerald-500" />
        </div>
        <h1 className="text-2xl font-700 text-foreground">Booking Confirmed!</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          We've sent a confirmation to{' '}
          <span className="font-600 text-foreground">{booking.customer?.email}</span>
        </p>
      </div>

      {/* Booking Ref Card */}
      <div className="bg-card rounded-xl border border-border shadow-elevated mb-6 overflow-hidden">
        <div className="booking-gradient px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-white/70 text-xs font-500 uppercase tracking-wider">Booking Reference</p>
            <p className="text-white text-2xl font-800 tracking-wider tabular-nums mt-0.5">{booking.bookingRef}</p>
          </div>
          <CheckCircle size={32} className="text-white/60" />
        </div>

        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
              <Car size={16} className="text-primary" />
            </div>
            <div>
              <p className="text-[11px] font-600 text-muted-foreground uppercase tracking-wider mb-0.5">Service</p>
              <p className="text-sm font-700 text-foreground">{booking.service?.name}</p>
              <p className="text-xs text-muted-foreground">${booking.service?.price} · {booking.service?.duration}min</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
              <Calendar size={16} className="text-primary" />
            </div>
            <div>
              <p className="text-[11px] font-600 text-muted-foreground uppercase tracking-wider mb-0.5">Appointment</p>
              <p className="text-sm font-700 text-foreground">{booking.date ? formatDate(booking.date) : '—'}</p>
              <p className="text-xs text-muted-foreground">{booking.timeSlot ? SLOT_LABELS[booking.timeSlot] : '—'}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
              <Mail size={16} className="text-primary" />
            </div>
            <div>
              <p className="text-[11px] font-600 text-muted-foreground uppercase tracking-wider mb-0.5">Customer</p>
              <p className="text-sm font-700 text-foreground">{booking.customer?.firstName} {booking.customer?.lastName}</p>
              <p className="text-xs text-muted-foreground">{booking.customer?.email}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
              <Car size={16} className="text-primary" />
            </div>
            <div>
              <p className="text-[11px] font-600 text-muted-foreground uppercase tracking-wider mb-0.5">Vehicle</p>
              <p className="text-sm font-700 text-foreground">
                {booking.customer?.vehicleYear} {booking.customer?.vehicleMake} {booking.customer?.vehicleModel}
              </p>
              <p className="text-xs text-muted-foreground capitalize">{booking.vehicleType}</p>
            </div>
          </div>
        </div>
      </div>

      {/* What to Expect */}
      <div className="bg-card rounded-xl border border-border shadow-card p-5 mb-6">
        <h3 className="text-sm font-700 text-foreground mb-4">What happens next</h3>
        <div className="flex flex-col gap-3">
          {[
            { icon: Mail, text: 'Confirmation email sent to your inbox with all booking details.' },
            { icon: Phone, text: 'We\'ll send a reminder 24 hours before your appointment.' },
            { icon: Clock, text: 'Arrive 5 minutes early. Payment is collected at the time of service.' },
          ].map((item, idx) => {
            const Icon = item.icon;
            return (
              <div key={`next-step-${idx}`} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon size={13} className="text-emerald-600" />
                </div>
                <p className="text-sm text-muted-foreground">{item.text}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button onClick={onNewBooking} className="btn-primary flex-1 flex items-center justify-center gap-2">
          <Plus size={15} />
          Book Another Service
        </button>
        <button className="btn-secondary flex-1 flex items-center justify-center gap-2">
          <Download size={15} />
          Download Confirmation
        </button>
        <Link href="/sign-up-login" className="btn-secondary flex-1 flex items-center justify-center gap-2">
          Create an Account
        </Link>
      </div>
    </div>
  );
}